import { Link, useNavigate } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import { useAddToServerCart } from "~/api/mutations/useAddToCart";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { useCartStore } from "~/lib/stores/cartStore";
import { cn } from "~/lib/utils";

import type { OpenPreinscriptionCourse } from "~/api/queries/useOpenPreinscriptionCourses";
import type { CartItem } from "~/lib/stores/cartStore";

/**
 * "Sumar al carrito" para un curso de PRE-INSCRIPCIÓN, con el look de la landing.
 *
 * Existe porque reservar de a uno era el único camino: para guardarse el lugar en cuatro
 * cursos había que hacer el flujo entero cuatro veces. Ahora se pueden juntar en el
 * carrito y resolverlos de una.
 *
 * Lo que NO hace, a propósito: cobrar. El curso tiene precio cargado (el de lanzamiento),
 * pero acá no se paga nada — el ítem viaja como reserva y el checkout registra la
 * pre-inscripción. Por eso el botón nunca muestra un precio: mostrarlo haría pensar que
 * el carrito va a cobrarlo.
 */
export function ReserveInCartButton({
  course,
  className,
  checkoutOnClick = false,
}: {
  course: OpenPreinscriptionCourse;
  className?: string;
  checkoutOnClick?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const addItemToStore = useCartStore((state) => state.addItem);
  const isInCart = useCartStore((state) => state.isInCart(course.id));
  const setCartSidebarOpen = useCartStore((state) => state.setCartSidebarOpen);
  const { mutateAsync: addToServer } = useAddToServerCart();

  const base =
    "flex w-full items-center justify-center gap-2 rounded-[10px] border-[1.5px] px-4 py-3 text-[13.5px] font-bold transition-all duration-150";

  if (isInCart) {
    return (
      <Link
        to={checkoutOnClick ? "/checkout" : "/cart"}
        data-cuelume-press
        className={cn(
          base,
          "border-landing-teal/45 bg-landing-teal-light text-landing-teal hover:border-landing-teal",
          className,
        )}
      >
        ✓ {t("landing.launch.next.inCartCta")}
      </Link>
    );
  }

  const handleAdd = async () => {
    const item: CartItem = {
      courseId: course.id,
      // Estos cursos no tienen slug público todavía: el detalle se resuelve por id.
      slug: null,
      title: course.title,
      thumbnailUrl: course.thumbnailUrl,
      authorName: "",
      categoryName: null,
      priceInCents: course.priceInCents,
      mercadopagoPriceInCents: course.mercadopagoPriceInCents ?? 0,
      currency: course.currency,
      mercadopagoProductId: course.mercadopagoProductId ?? null,
      hasFreeChapters: false,
      isPreinscription: true,
      addedAt: new Date().toISOString(),
    };

    addItemToStore(item);

    if (currentUser) {
      try {
        await addToServer(course.id);
      } catch {
        // El carrito local ya lo tiene; se sincroniza al loguear/reintentar.
      }
    }

    if (checkoutOnClick) navigate("/checkout");
    else setCartSidebarOpen(true);
  };

  return (
    <button
      type="button"
      onClick={handleAdd}
      data-cuelume-press
      data-cuelume-release
      className={cn(
        base,
        "border-landing-border bg-landing-white text-landing-text hover:-translate-y-0.5 hover:border-landing-red hover:text-landing-red",
        className,
      )}
    >
      {checkoutOnClick
        ? t("landing.launch.next.preCta")
        : `🛒 ${t("landing.launch.next.addToCartCta")}`}
    </button>
  );
}
